"use client"

import type { ComponentProps } from "react"
import { Badge } from "~/components/legacy/common/badge"
import { Card, CardDescription, CardHeader } from "~/components/legacy/common/card"
import { H4 } from "~/components/legacy/common/heading"
import { Link } from "~/components/legacy/common/link"
import { ShowMore } from "~/components/legacy/common/show-more"
import { Skeleton } from "~/components/legacy/common/skeleton"
import { Stack } from "~/components/legacy/common/stack"
import { Favicon } from "~/components/legacy/web/ui/favicon"
import { ToolRepositoryStats } from "~/components/web/tools/tool-repository-stats"
import { DELIVERY_MODEL_LABELS, TOOL_CARD_TYPE_LABELS } from "~/lib/catalog-labels"
import { cx } from "~/lib/legacy-utils"
import type { ToolMany } from "~/server/web/tools/payloads"

type ToolCardProps = ComponentProps<typeof Card> & {
  tool: ToolMany
  reasonLabels?: readonly string[]
}

const ToolCard = ({ tool, reasonLabels = [], className, ...props }: ToolCardProps) => {
  const implementation = tool.repositories.find(
    repo => repo.repoRole === "ProductImplementation" && repo.qualificationState === "Approved",
  )
  const primaryRepository = tool.entityType !== "Product" ? implementation?.repository : null

  return (
    <Card {...props} className={cx("gap-3 p-4", className)}>
      <CardHeader wrap={false}>
        <Favicon src={tool.faviconUrl} title={tool.name} contained />

        <H4 as="h3" className="truncate">
          <Link href={`/tools/${tool.slug}`}>
            <span className="absolute inset-0 z-10" />
            {tool.name}
          </Link>
        </H4>
      </CardHeader>

      <div className="relative flex w-full flex-1 flex-col">
        <Stack
          size="md"
          direction="column"
          wrap={false}
          className="flex-1 duration-200 group-hover:opacity-0"
        >
          {tool.tagline && <CardDescription className="min-h-10">{tool.tagline}</CardDescription>}

          <p className="min-h-8 text-xs text-muted-foreground">
            {TOOL_CARD_TYPE_LABELS[tool.entityType] ?? "Tool"}
            {tool.lifecycleStatus === "Retired"
              ? " · Retired"
              : tool.lifecycleStatus === "Legacy"
                ? " · Legacy API"
                : ""}
            {tool.deliveryModels?.length
              ? ` · ${tool.deliveryModels.map(model => DELIVERY_MODEL_LABELS[model] ?? model).join(" / ")}`
              : ""}
          </p>
          <div className="min-h-4 w-full">
            {primaryRepository && <ToolRepositoryStats repository={primaryRepository} />}
          </div>

          {reasonLabels.length > 0 && (
            <Stack size="xs" className="-mt-1">
              {reasonLabels.map(label => (
                <Badge key={label} variant="info" className="max-w-full whitespace-normal">
                  {label}
                </Badge>
              ))}
            </Stack>
          )}
        </Stack>

        {tool.description && (
          <div className="absolute inset-0 opacity-0 duration-200 group-hover:opacity-100">
            <CardDescription className="line-clamp-3">{tool.description}</CardDescription>
          </div>
        )}
      </div>

      <ShowMore
        items={tool.categories}
        limit={1}
        buffer={0}
        getKey={category => category.name}
        renderItem={({ name }) => <Badge variant="outline">{name}</Badge>}
        size="xs"
        showMoreType="text"
        className="mt-auto"
      />
    </Card>
  )
}

const ToolCardSkeleton = () => {
  return (
    <Card hover={false} className="gap-3 p-4 items-stretch select-none">
      <CardHeader>
        <Favicon src="/favicon.svg" className="animate-pulse opacity-25 grayscale" contained />

        <H4 className="w-2/3">
          <Skeleton>&nbsp;</Skeleton>
        </H4>
      </CardHeader>

      <CardDescription className="flex flex-col gap-0.5">
        <Skeleton className="h-5 w-4/5">&nbsp;</Skeleton>
        <Skeleton className="h-5 w-1/2">&nbsp;</Skeleton>
      </CardDescription>

      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-4 w-3/5" />

      <Stack size="sm" className="mt-auto">
        {Array.from({ length: 2 }, (_, i) => `badge-skeleton-${i}`).map(id => (
          <Badge key={id} variant="outline" className="w-12">
            &nbsp;
          </Badge>
        ))}
      </Stack>
    </Card>
  )
}

export { ToolCard, ToolCardSkeleton }
